import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable, Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { SupplierViewModal } from '../components/procurement/SupplierViewModal';
import { mockPRs } from '../data/mockData';
import { XIcon, EyeIcon } from 'lucide-react';
const mockRFQBidders = [{
  id: '1',
  supplierId: 'SUP-001',
  supplierName: 'TechCorp Solutions',
  invitationDate: '2023-10-20',
  submissionStatus: 'Submitted',
  technicalScore: 85,
  financialScore: 90,
  totalScore: 87.5,
  responseStatus: 'Evaluated',
  documents: ['quotation.pdf', 'technical_specs.pdf'],
  quotedAmount: 45000,
  deliveryTimeline: '4 Weeks'
}, {
  id: '2',
  supplierId: 'SUP-002',
  supplierName: 'Global Logistics Inc',
  invitationDate: '2023-10-20',
  submissionStatus: 'Pending',
  responseStatus: 'Pending',
  documents: []
}];
export function RFQList() {
  const navigate = useNavigate();
  const [isBidderModalOpen, setIsBidderModalOpen] = useState(false);
  const [selectedPR, setSelectedPR] = useState<any>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const data = mockPRs.filter(pr => pr.status === 'Approved' && pr.procurementMethod === 'Short Purchase');
  const handleOpenBidders = (pr: any) => {
    setSelectedPR(pr);
    setIsBidderModalOpen(true);
  };
  const handleViewSupplier = (supplier: any) => {
    setSelectedSupplier(supplier);
    setIsSupplierModalOpen(true);
  };
  const bidderColumns: Column<any>[] = [{
    key: 'supplierName',
    label: 'Supplier Name'
  }, {
    key: 'invitationDate',
    label: 'Invitation Date'
  }, {
    key: 'submissionStatus',
    label: 'Submission',
    render: item => <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.submissionStatus === 'Submitted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-2284">
          {item.submissionStatus}
        </span>
  }, {
    key: 'technicalScore',
    label: 'Tech Score',
    render: item => <span className="text-sm" data-id="element-2285">{item.technicalScore ?? '—'}</span>
  }, {
    key: 'financialScore',
    label: 'Fin Score',
    render: item => <span className="text-sm" data-id="element-2286">{item.financialScore ?? '—'}</span>
  }, {
    key: 'totalScore',
    label: 'Total',
    render: item => <span className="text-sm font-semibold text-indigo-600" data-id="element-2287">
          {item.totalScore ?? '—'}
        </span>
  }];
  const columns: Column<any>[] = [{
    key: 'prNo',
    label: 'PR No',
    render: pr => <span className="font-medium text-indigo-600" data-id="element-2288">{pr.prNo}</span>
  }, {
    key: 'originator',
    label: 'Originator'
  }, {
    key: 'requisitionFor',
    label: 'Requisition For'
  }, {
    key: 'procurementMethod',
    label: 'Method'
  }, {
    key: 'total',
    label: 'Total Amount',
    render: pr => `$${pr.total.toLocaleString()}`
  }, {
    key: 'status',
    label: 'PR Status',
    render: pr => <StatusBadge status={pr.status} data-id="element-2289" />
  }, {
    key: 'rfqStatus',
    label: 'RFQ Status',
    render: () => <StatusBadge status="Draft" data-id="element-2290" />
  }, {
    key: 'actions',
    label: 'Action',
    render: pr => <div className="flex items-center justify-end space-x-3" data-id="element-2291">
          <button onClick={() => handleOpenBidders(pr)} className="text-amber-600 hover:text-amber-900 font-medium text-sm" data-id="element-2292">
            Bidder
          </button>
          <button onClick={() => navigate(`/rfq/${pr.id}`)} className="text-indigo-600 hover:text-indigo-900 font-medium text-sm" data-id="element-2293">
            Process RFQ
          </button>
        </div>
  }];
  return <div className="space-y-6 h-full flex flex-col pb-8" data-id="element-2294">
      <div className="flex items-center justify-between" data-id="element-2295">
        <div data-id="element-2296">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2297">
            Request for Quotation (RFQ)
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2298">
            Process RFQ for approved Short Purchase requisitions.
          </p>
        </div>
      </div>

      <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col" data-id="element-2299">
        <div className="flex-1 p-0" data-id="element-2300">
          <DataTable columns={columns} data={data} keyExtractor={item => item.id} searchPlaceholder="Search PRs..." data-id="element-2301" />
        </div>
      </div>

      {/* Bidders Modal — Suppliers & Evaluation */}
      {isBidderModalOpen && selectedPR && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-2302">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0" data-id="element-2303">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setIsBidderModalOpen(false)} data-id="element-2304"></div>
            <div className="relative inline-block w-full max-w-5xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6" data-id="element-2305">
              <div className="absolute top-0 right-0 pt-4 pr-4" data-id="element-2306">
                <button className="text-gray-400 bg-white rounded-md hover:text-gray-500" onClick={() => setIsBidderModalOpen(false)} data-id="element-2307">
                  <XIcon className="w-6 h-6" data-id="element-2308" />
                </button>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-6" data-id="element-2309">
                Suppliers & Evaluation — {selectedPR.prNo}
              </h3>
              <DataTable columns={bidderColumns} data={mockRFQBidders} keyExtractor={item => item.id} searchPlaceholder="Search suppliers..." actions={item => <button onClick={() => handleViewSupplier(item)} className="inline-flex items-center text-indigo-600 hover:text-indigo-900 font-medium text-sm" data-id="element-2311">
                    <EyeIcon className="h-4 w-4 mr-1" data-id="element-2312" />
                    View
                  </button>} data-id="element-2310" />
            </div>
          </div>
        </div>}

      <SupplierViewModal isOpen={isSupplierModalOpen} onClose={() => setIsSupplierModalOpen(false)} supplier={selectedSupplier} type="RFQ" data-id="element-2313" />
    </div>;
}